/**
 * Donut (Phase 3, §6.3/§6.4) — SPINE. A single-month category breakdown as SVG
 * arcs with a centre hero (total Ausgaben). Slice colour follows the entity
 * (`bucket.color`, palette fallback resolved by the caller); the caller passes a
 * ranked legend (BucketChip + amount + pct). 2px surface gap between slices.
 */
import { useState } from 'react';
import { money } from '../../../lib/format.ts';
import { ChartFrame } from './ChartFrame.tsx';
import { Tooltip } from './Tooltip.tsx';
import type { ReactNode } from 'react';

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

export interface DonutProps {
  title: string;
  subtitle?: string;
  description?: string;
  slices: DonutSlice[];
  /** Centre caption above the hero value, e.g. `Ausgaben gesamt`. */
  centerLabel?: string;
  /** Pre-formatted centre hero value (default: `money(total)`). */
  centerValue?: string;
  /** Ranked legend (FE-A builds this from BucketChip + amount + pct). */
  legend?: ReactNode;
  formatValue?: (n: number) => string;
  aspect?: number;
}

const GAP_DEG = 1.5;

function polar(cx: number, cy: number, r: number, angleDeg: number): [number, number] {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function arcPath(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  start: number,
  end: number,
): string {
  const [x1, y1] = polar(cx, cy, rOuter, start);
  const [x2, y2] = polar(cx, cy, rOuter, end);
  const [x3, y3] = polar(cx, cy, rInner, end);
  const [x4, y4] = polar(cx, cy, rInner, start);
  const large = end - start > 180 ? 1 : 0;
  return `M${x1},${y1} A${rOuter},${rOuter} 0 ${large} 1 ${x2},${y2} L${x3},${y3} A${rInner},${rInner} 0 ${large} 0 ${x4},${y4} Z`;
}

export function Donut({
  title,
  subtitle,
  description,
  slices,
  centerLabel,
  centerValue,
  legend,
  formatValue = money,
  aspect = 0.62,
}: DonutProps) {
  const [hover, setHover] = useState<number | null>(null);

  const total = slices.reduce((acc, s) => acc + s.value, 0);
  const heroValue = centerValue ?? formatValue(total);

  const columns = ['Kategorie', 'Betrag', 'Anteil'];
  const rows: (string | number)[][] = slices.map((s) => [
    s.label,
    formatValue(s.value),
    total > 0 ? `${((s.value / total) * 100).toFixed(1).replace('.', ',')} %` : '–',
  ]);

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      description={description}
      aspect={aspect}
      legend={legend}
      table={{ columns, rows, caption: title }}
    >
      {({ width, height }) => {
        const cx = width / 2;
        const cy = height / 2;
        const rOuter = Math.max(0, Math.min(width, height) / 2 - 8);
        const rInner = rOuter * 0.62;

        if (total <= 0 || slices.length === 0) {
          return (
            <g>
              <circle cx={cx} cy={cy} r={rOuter} fill="none" stroke="var(--viz-grid)" strokeWidth={2} />
              <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={12} fill="var(--viz-label)">
                Keine Daten
              </text>
            </g>
          );
        }

        // Cumulative angles, with a small gap between slices.
        let cursor = 0;
        const segments = slices.map((s, i) => {
          const sweep = (s.value / total) * 360;
          const gap = slices.length > 1 ? GAP_DEG : 0;
          const start = cursor + gap / 2;
          const end = cursor + sweep - gap / 2;
          cursor += sweep;
          const mid = (start + end) / 2;
          const [lx, ly] = polar(cx, cy, (rOuter + rInner) / 2, mid);
          return { slice: s, path: arcPath(cx, cy, rOuter, rInner, start, Math.max(start, end)), lx, ly, index: i };
        });

        return (
          <g onPointerLeave={() => setHover(null)}>
            {segments.map(({ slice, path, index }) => (
              <path
                key={slice.key}
                d={path}
                fill={slice.color}
                stroke="var(--viz-surface)"
                strokeWidth={2}
                opacity={hover === null || hover === index ? 1 : 0.5}
                onPointerEnter={() => setHover(index)}
              />
            ))}

            {centerLabel ? (
              <text x={cx} y={cy - 12} textAnchor="middle" fontSize={11} fill="var(--viz-label)">
                {centerLabel}
              </text>
            ) : null}
            <text
              x={cx}
              y={centerLabel ? cy + 8 : cy}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={18}
              fontWeight={700}
              fill="var(--viz-tooltip-fg)"
            >
              {heroValue}
            </text>

            {hover !== null && segments[hover] ? (
              <Tooltip
                x={segments[hover].lx}
                y={segments[hover].ly}
                title={segments[hover].slice.label}
                chartWidth={width}
                rows={[
                  { label: 'Betrag', value: formatValue(segments[hover].slice.value) },
                  {
                    label: 'Anteil',
                    value: `${((segments[hover].slice.value / total) * 100).toFixed(1).replace('.', ',')} %`,
                  },
                ]}
              />
            ) : null}
          </g>
        );
      }}
    </ChartFrame>
  );
}
